import { Geolocation } from '@capacitor/geolocation';
import { isNativePlatform, waitForNativePlugin } from './capacitor';

export interface DeviceCoordinates {
  latitude: number;
  longitude: number;
}

interface CityCache {
  name: string;
  savedAt: number;
}

interface ReverseGeocodeAdmin {
  adminLevel?: number;
  name?: string;
  description?: string;
}

interface ReverseGeocodeResponse {
  city?: string;
  locality?: string;
  principalSubdivision?: string;
  localityInfo?: {
    administrative?: ReverseGeocodeAdmin[];
  };
}

const GEO_PLUGIN = 'Geolocation';
const CITY_CACHE_KEY = 'sporto_device_city';
const CITY_CACHE_TTL_MS = 6 * 60 * 60 * 1000;

function readCityCache(): CityCache | null {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  const raw = localStorage.getItem(CITY_CACHE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as CityCache;
    if (!parsed.name || typeof parsed.savedAt !== 'number') {
      return null;
    }
    if (Date.now() - parsed.savedAt > CITY_CACHE_TTL_MS) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function getCachedCityName(): string | null {
  return readCityCache()?.name ?? null;
}

function saveCachedCityName(name: string): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  const payload: CityCache = { name, savedAt: Date.now() };
  localStorage.setItem(CITY_CACHE_KEY, JSON.stringify(payload));
}

export async function prepareGeolocation(): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }
  try {
    await waitForNativePlugin(GEO_PLUGIN);
  } catch {
    return;
  }
}

async function ensureNativeLocationGranted(): Promise<boolean> {
  const available = await waitForNativePlugin(GEO_PLUGIN);
  if (!available) {
    return false;
  }

  const current = await Geolocation.checkPermissions();
  if (current.location === 'granted') {
    return true;
  }
  if (current.location === 'denied') {
    return false;
  }

  const requested = await Geolocation.requestPermissions();
  return requested.location === 'granted';
}

function getBrowserCoordinates(): Promise<DeviceCoordinates | null> {
  if (!('geolocation' in navigator)) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  });
}

export async function getCurrentCoordinates(): Promise<DeviceCoordinates | null> {
  try {
    if (isNativePlatform()) {
      const granted = await ensureNativeLocationGranted();
      if (!granted) {
        return null;
      }
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: 300000,
      });
      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
    }

    return await getBrowserCoordinates();
  } catch {
    return null;
  }
}

export async function reverseGeocodeCity(lat: number, lon: number): Promise<string> {
  try {
    const response = await fetch(
      `https://api-bdc.io/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=id`
    );
    const data = (await response.json()) as ReverseGeocodeResponse | null;
    if (!data) {
      return '';
    }

    const administrative = data.localityInfo?.administrative || [];
    const cityLevel = administrative.find((admin) => admin.adminLevel === 5);

    if (cityLevel?.name) {
      const description = cityLevel.description?.toLowerCase() ?? '';
      if (description.includes('kota')) {
        return `Kota ${cityLevel.name}`;
      }
      if (description.includes('kabupaten')) {
        return `Kab. ${cityLevel.name}`;
      }
      return cityLevel.name;
    }

    return data.city || data.locality || data.principalSubdivision || '';
  } catch (error) {
    console.error('Failed to reverse geocode location:', error);
    return '';
  }
}

export async function resolveDeviceCityName(): Promise<string | null> {
  const cached = getCachedCityName();
  const coords = await getCurrentCoordinates();
  if (!coords) {
    return cached;
  }

  const city = await reverseGeocodeCity(coords.latitude, coords.longitude);
  if (city) {
    saveCachedCityName(city);
    return city;
  }

  return cached;
}

export async function requestGeolocationPermission(): Promise<boolean> {
  if (!isNativePlatform()) {
    return false;
  }
  const status = await Geolocation.requestPermissions();
  return status.location === 'granted';
}
